import { Spinner, ThemeProvider } from "@cxc/react-fpds-components";
import { defaultTheme } from "@cxc/react-fpds-foundation";
import { AxiosResponse } from "axios";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getWithRetry } from "../../clients/ApiClient";
import { createProxyUrlForEndpoint } from "../../utils";
import ErrorNotLoad from "../error/BaseErrorPage";
import { DealerLookupModal } from "./DealerLookUpModal";
import {
  DealerContextProviderProps,
  DealerContextType,
  MinimalDealer,
} from "./DealerTypes";

export const DealerContext = createContext({
  dealerExternalId: "",
  setDealerExternalId: () => {},
  dealer: undefined as MinimalDealer | undefined,
  setDealer: () => {},
  initDealer: () => {},
  dealers: [] as MinimalDealer[],
} as DealerContextType);

export const DealerContextProvider: React.FC<DealerContextProviderProps> = ({
  children,
  baseUrl,
  aemBaseUrl,
}) => {
  const DEALER_DIRECTORY_PATH = "/dealer-directory/dealer";
  const DEALER_DIRECTORY_LIST_PATH = "/dealer-directory/dealer/list";

  const dealerListEndpoint = createProxyUrlForEndpoint(
    baseUrl,
    DEALER_DIRECTORY_LIST_PATH,
  );
  const dealerEndpoint = createProxyUrlForEndpoint(
    baseUrl,
    DEALER_DIRECTORY_PATH,
  );

  const [dealerExternalId, setDealerExternalId] = useState<string>("");

  const [dealer, setDealer] = useState<MinimalDealer | undefined>();

  const [dealers, setDealers] = useState<MinimalDealer[]>([]);

  const [error, setError] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const initDealer = useCallback(() => {
    if (dealerExternalId !== "") {
      getDealer(dealerEndpoint, dealerExternalId)
        .then((dealer) => {
          setDealer(dealer);
          setError(false);
        })
        .catch(() => {
          setError(true);
        });
    }
  }, [dealerExternalId]);

  const updateDealerList = () => {
    setIsLoading(true);
    getDealerList(dealerListEndpoint)
      .then((qds) => {
        setIsLoading(false);
        if (qds.length === 0) {
          setError(true);
        } else if (qds.length === 1 && dealers.length === 0) {
          setError(false);
          setDealerExternalId(qds[0].externalId);
        } else if (qds.length > 1) {
          setError(false);
          setDealers(
            qds.sort((a, b) =>
              a.contactInfo.name.localeCompare(b.contactInfo.name),
            ),
          );
        }
      })
      .catch(() => {
        setIsLoading(false);
        setError(true);
        setDealers([]);
      });
  };

  const getDealerList = async (dealerListEndpoint: string) => {
    try {
      const response: AxiosResponse = await getWithRetry(dealerListEndpoint, {
        timeout: 20000,
        shouldRetryWhenTimeout: false,
        retryWait: [2000],
        errorsToRetry: [401],
        method: "get",
      });

      if (response.status < 200 || response.status >= 300)
        throw new Error("Invalid http status: " + response.status);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return response?.data?.data as MinimalDealer[];
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const getDealer = async (
    getDealerEndpoint: string,
    externalId?: string,
  ): Promise<MinimalDealer> => {
    const response: AxiosResponse = await getWithRetry(
      `${getDealerEndpoint}?externalId=${externalId}`,
      {
        timeout: 20000,
        shouldRetryWhenTimeout: false,
        retryWait: [2000],
        errorsToRetry: [401],
        method: "get",
      },
    );

    if (response.status < 200 || response.status >= 300)
      throw new Error("Invalid http status: " + response.status);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return response?.data?.data as MinimalDealer;
  };

  useEffect(() => {
    updateDealerList();
  }, []);

  useEffect(() => {
    initDealer();
  }, [dealerExternalId]);

  const renderComponent = () => {
    return (
      <ThemeProvider theme={defaultTheme}>
        {isLoading ? (
          <Spinner
            size="large"
            style={{
              top: "30%",
              left: "50%",
              position: "fixed",
            }}
            data-testid={"loading-spinner"}
          />
        ) : dealers.length > 0 && !dealer ? (
          <DealerLookupModal
            dealers={dealers}
            setDealerExternalId={(val: string) => setDealerExternalId(val)}
            dealerExternalId={dealerExternalId}
            aemBaseUrl={aemBaseUrl}
          />
        ) : (
          children
        )}
      </ThemeProvider>
    );
  };

  const value = useMemo(
    () => ({
      dealerExternalId,
      setDealerExternalId,
      dealer,
      setDealer,
      initDealer,
      dealers,
    }),
    [
      dealerExternalId,
      setDealerExternalId,
      dealer,
      setDealer,
      initDealer,
      dealers,
    ],
  );

  return (
    <DealerContext.Provider value={value}>
      {error ? <ErrorNotLoad /> : renderComponent()}
    </DealerContext.Provider>
  );
};
